import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { CartDrawer } from "@/components/CartDrawer";

const NAV = [
  { label: "NEW IN", to: "/collection/new-arrivals" },
  { label: "SHOP", to: "/shop" },
  { label: "BEST SELLERS", to: "/collection/best-sellers" },
  { label: "ABOUT", to: "/about" },
  { label: "CONTACT", to: "/contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const { count, setOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/shop?search=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  return (
    <>
      {/* Announcement bar */}
      <div className="bg-ink text-ivory text-center text-[0.62rem] tracking-[0.28em] uppercase py-2.5 px-4">
        Complimentary shipping over $75 · Anti-tarnish guarantee
      </div>

      <header
        className={`sticky top-0 z-40 transition-colors duration-500 ${
          scrolled ? "bg-ivory/80 backdrop-blur-xl border-b border-black/5" : "bg-ivory"
        }`}
        data-testid="navbar"
      >
        <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 md:px-10">
          <div className="flex items-center gap-3 md:w-1/3">
            <button
              className="md:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-open"
            >
              <Menu size={22} strokeWidth={1.5} />
            </button>
          </div>

          <Link
            to="/"
            className="font-serif text-2xl tracking-[0.18em] text-ink md:text-3xl md:w-1/3 md:text-center"
            data-testid="logo"
          >
            NOORA
          </Link>

          <div className="flex items-center justify-end gap-4 md:w-1/3 md:gap-5">
            <button onClick={() => setSearchOpen((s) => !s)} aria-label="Search" data-testid="search-toggle">
              <Search size={19} strokeWidth={1.5} />
            </button>
            <Link
              to={user ? "/account" : "/login"}
              aria-label="Account"
              data-testid="account-link"
              className="hidden sm:block"
            >
              <User size={19} strokeWidth={1.5} />
            </Link>
            <Link to="/wishlist" aria-label="Wishlist" data-testid="wishlist-link" className="relative">
              <Heart size={19} strokeWidth={1.5} />
              {wishCount > 0 && (
                <span className="absolute -right-2 -top-2 grid h-4 w-4 place-items-center rounded-full bg-gold text-[0.55rem] text-ivory">
                  {wishCount}
                </span>
              )}
            </Link>
            <button onClick={() => setOpen(true)} aria-label="Shopping bag" data-testid="cart-toggle" className="relative">
              <ShoppingBag size={19} strokeWidth={1.5} />
              {count > 0 && (
                <span className="absolute -right-2 -top-2 grid h-4 w-4 place-items-center rounded-full bg-ink text-[0.55rem] text-ivory">
                  {count}
                </span>
              )}
            </button>
          </div>
        </nav>

        {/* Desktop menu row */}
        <div className="hidden justify-center gap-10 border-t border-black/5 py-3 md:flex">
          {NAV.map((n) => (
            <Link
              key={n.label}
              to={n.to}
              className="eyebrow link-underline text-ink/80 hover:text-ink"
              data-testid={`nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {n.label}
            </Link>
          ))}
        </div>

        {/* Search bar */}
        <AnimatePresence>
          {searchOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden border-t border-black/5 bg-ivory"
            >
              <form onSubmit={submitSearch} className="mx-auto flex max-w-2xl items-center gap-3 px-5 py-5">
                <Search size={18} strokeWidth={1.5} className="text-muted-foreground" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search for necklaces, hoops, rings…"
                  className="w-full bg-transparent text-sm tracking-wide outline-none placeholder:text-muted-foreground"
                  data-testid="search-input"
                />
                <button type="submit" className="eyebrow text-ink" data-testid="search-submit">
                  Search
                </button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-50 bg-ink/40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              className="fixed left-0 top-0 z-50 h-full w-[80%] max-w-xs bg-ivory p-8"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              data-testid="mobile-menu"
            >
              <div className="mb-10 flex items-center justify-between">
                <span className="font-serif text-2xl tracking-[0.18em]">NOORA</span>
                <button onClick={() => setMobileOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close">
                  <X size={22} strokeWidth={1.5} />
                </button>
              </div>
              <div className="flex flex-col gap-6">
                {NAV.map((n) => (
                  <Link
                    key={n.label}
                    to={n.to}
                    onClick={() => setMobileOpen(false)}
                    className="font-serif text-3xl text-ink"
                  >
                    {n.label}
                  </Link>
                ))}
                <Link
                  to={user ? "/account" : "/login"}
                  onClick={() => setMobileOpen(false)}
                  className="mt-6 eyebrow text-ink/70"
                >
                  {user ? "My Account" : "Login / Register"}
                </Link>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <CartDrawer />
    </>
  );
}
